import React, { useEffect, useState } from "react";
import {
  FormSection,
  SectionTitle,
  FormGroup,
  Label,
  Input,
  PhoneInputGroup,
  SearchButton,
  ButtonContent,
  LoadingSpinner,
  ErrorMessage,
  PatientInfoCard,
  PatientInfoHeader,
  PatientInfoContent,
  PatientInfoItem,
  PatientInfoLabel,
  PatientInfoValue
} from "../../styles/OrderFormStyles";
import { updatePatient } from "../../services/api";

// Utility function to extract only digits from a string
const extractDigits = (input) => {
  if (!input) return "";

  // Check if the input starts with a plus sign
  const hasPlus = input.startsWith("+");

  // Remove all non-digit characters
  const digits = input.replace(/\D/g, "");

  // Return with the plus sign if the original input had one
  return hasPlus ? "+" + digits : digits;
};

// Patient.gender stores words; staff see the short form the pharmacies use.
const GENDER_DISPLAY = {
  Male: "M",
  Female: "F",
  Unspecified: "Unknown"
};

const PatientInfoSection = ({
  editMode,
  originalOrderData,
  formData,
  setFormData,
  errors,
  setErrors,
  loading,
  patientFound,
  setPatientFound,
  patientAddresses,
  setPatientAddresses,
  states,
  loadPharmaciesAndMedicines,
  handlePatientSearch,
  onEditUnsavedChange
}) => {
  // Track the display version of the phone (with formatting)
  const [displayPhone, setDisplayPhone] = React.useState("");

  // Edit mode state for patient info
  const [isEditing, setIsEditing] = useState(false);
  const [editFields, setEditFields] = useState({ first_name: "", last_name: "", email: "", date_of_birth: "", gender: "" });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Reset edit state when patient changes (e.g. new patient searched)
  useEffect(() => {
    setIsEditing(false);
    setSaveError("");
    onEditUnsavedChange?.(false);
  }, [formData.patientId]);

  // Initialize form data from originalOrderData when in edit mode
  useEffect(() => {
    if (editMode && originalOrderData) {
      // Set the patient found flag for edit mode
      setPatientFound(true);

      // Extract data from the original order
      const {
        patient_id,
        patient_name,
        addresses,
        patient_email,
        patient_phone,
        shipping_address
      } = originalOrderData;

      // Store the formatted display version
      setDisplayPhone(patient_phone || "");

      // Set up shipping address from original order
      const addressId = shipping_address?.address_id;

      // Update patientAddresses with shipping address if available and not already in the list
      if (
        shipping_address &&
        !patientAddresses.some((addr) => addr.address_id === addressId)
      ) {
        setPatientAddresses((prev) => [...prev, ...addresses]);
      }

      // Find the matching state for the shipping address
      const matchedState = states.find(
        (state) =>
          state.state_name.toLowerCase() ===
            shipping_address?.state.toLowerCase() ||
          state.state_abbreviation.toLowerCase() ===
            shipping_address?.state.toLowerCase()
      );

      // Update form data with patient info from originalOrderData
      setFormData((prevData) => {
        // Skip update if the data is already set to avoid infinite loop
        if (
          prevData.patientId === patient_id &&
          prevData.selectedAddressId === addressId
        ) {
          return prevData;
        }

        return {
          ...prevData,
          patientId: patient_id,
          patientName: patient_name,
          patientEmail: patient_email,
          patientPhone: extractDigits(patient_phone), // Store only digits
          selectedAddressId: addressId || "",
          shipping_address_id: addressId || "",
          patientAddress: shipping_address
            ? {
                id: shipping_address.address_id,
                street: shipping_address.address1,
                apt: shipping_address.address2 || "",
                city: shipping_address.city,
                state: shipping_address.state,
                zipcode: shipping_address.zipcode,
                imported: shipping_address.imported
              }
            : prevData.patientAddress,
          // Store the full shipping_address object
          shipping_address: shipping_address || prevData.shipping_address,
          stateId: matchedState?.state_id?.toString() || prevData.stateId
        };
      });

      const currentStateId = formData.stateId;
      const newStateId = matchedState?.state_id?.toString();
      if (
        matchedState &&
        matchedState.state_id &&
        (!currentStateId || currentStateId !== newStateId)
      ) {
        loadPharmaciesAndMedicines(matchedState.state_id.toString());
      }
    }
  }, [
    editMode,
    originalOrderData,
    states,
    setFormData,
    setPatientFound,
    patientAddresses,
    setPatientAddresses
  ]);

  // Custom handler for phone input changes
  const handlePhoneChange = (e) => {
    const inputValue = e.target.value;
    const digitsOnly = extractDigits(inputValue);

    // Store the full input for display purposes
    setDisplayPhone(inputValue);

    // Store only digits in formData for API calls
    setFormData((prev) => ({
      ...prev,
      patientPhone: digitsOnly
    }));

    // Clear any errors
    if (errors.patientPhone) {
      setErrors((prev) => {
        const { patientPhone, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleEdit = () => {
    const nameParts = (formData.patientName || "").trim().split(" ");
    setEditFields({
      first_name: formData.patientFirstName || nameParts[0] || "",
      last_name: formData.patientLastName || nameParts.slice(1).join(" ") || "",
      email: formData.patientEmail || "",
      date_of_birth: formData.patientDOB || "",
      gender: formData.patientGender || "Unspecified"
    });
    setSaveError("");
    setIsEditing(true);
    onEditUnsavedChange?.(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSaveError("");
    onEditUnsavedChange?.(false);
  };

  const handleSave = async () => {
    if (!editFields.first_name.trim() || !editFields.last_name.trim()) {
      setSaveError("First name and last name are required.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      await updatePatient(formData.patientId, {
        first_name: editFields.first_name.trim(),
        last_name: editFields.last_name.trim(),
        email: editFields.email.trim(),
        date_of_birth: editFields.date_of_birth || null,
        gender: editFields.gender || "Unspecified"
      });
      setFormData((prev) => ({
        ...prev,
        patientFirstName: editFields.first_name.trim(),
        patientLastName: editFields.last_name.trim(),
        patientName: `${editFields.first_name.trim()} ${editFields.last_name.trim()}`,
        patientEmail: editFields.email.trim(),
        patientDOB: editFields.date_of_birth || prev.patientDOB,
        patientGender: editFields.gender || "Unspecified"
      }));
      setIsEditing(false);
      onEditUnsavedChange?.(false);
    } catch (err) {
      setSaveError(err.response?.data?.error || err.response?.data?.email?.[0] || "Failed to save patient info.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormSection>
      <SectionTitle>Patient Information</SectionTitle>

      <FormGroup>
        <Label htmlFor="patientPhone">Patient Phone Number</Label>
        <PhoneInputGroup>
          <Input
            type="text"
            id="patientPhone"
            name="patientPhone"
            value={displayPhone} // Show what the user typed (with formatting)
            onChange={handlePhoneChange} // Use custom handler
            placeholder="(xxx) xxx-xxxx"
          />
          <SearchButton
            type="button"
            onClick={handlePatientSearch}
            disabled={loading.searchingPatient || !formData.patientPhone}
          >
            {loading.searchingPatient ? (
              <ButtonContent>
                <LoadingSpinner />
                Searching...
              </ButtonContent>
            ) : (
              "Search"
            )}
          </SearchButton>
        </PhoneInputGroup>
        {errors.patientPhone && (
          <ErrorMessage>{errors.patientPhone}</ErrorMessage>
        )}
        <small
          style={{
            color: "var(--text-secondary, #aaa)",
            fontSize: "0.85rem",
            marginTop: "0.25rem",
            display: "block"
          }}
        >
          You can enter phone numbers in any format - only the digits will be
          used for searching.
        </small>
      </FormGroup>

      {patientFound && (
        <PatientInfoCard>
          <PatientInfoHeader style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            Patient Information
            {!isEditing && (
              <button
                type="button"
                onClick={handleEdit}
                style={{
                  padding: "0.25rem 0.75rem",
                  fontSize: "0.8rem",
                  background: "var(--primary)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer"
                }}
              >
                Edit
              </button>
            )}
          </PatientInfoHeader>

          {isEditing ? (
            <div style={{ padding: "0.5rem 0" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={editFields.first_name}
                    onChange={(e) => setEditFields((p) => ({ ...p, first_name: e.target.value }))}
                    placeholder="First name"
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={editFields.last_name}
                    onChange={(e) => setEditFields((p) => ({ ...p, last_name: e.target.value }))}
                    placeholder="Last name"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editFields.email}
                    onChange={(e) => setEditFields((p) => ({ ...p, email: e.target.value }))}
                    placeholder="Email"
                  />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={editFields.date_of_birth}
                    onChange={(e) => setEditFields((p) => ({ ...p, date_of_birth: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Gender</Label>
                  <select
                    value={editFields.gender}
                    onChange={(e) => setEditFields((p) => ({ ...p, gender: e.target.value }))}
                    style={{
                      width: "100%",
                      padding: "0.5rem 0.75rem",
                      fontSize: "1rem",
                      borderRadius: "4px",
                      border: "1px solid #ced4da",
                      background: "var(--input-bg, #fff)",
                      color: "var(--text-color, #000)",
                      height: "38px"
                    }}
                  >
                    {Object.entries(GENDER_DISPLAY).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    type="text"
                    value={formData.patientPhone}
                    readOnly
                    disabled
                    style={{ opacity: 0.6, cursor: "not-allowed" }}
                  />
                </div>
              </div>
              {saveError && <ErrorMessage style={{ marginBottom: "0.5rem" }}>{saveError}</ErrorMessage>}
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <SearchButton type="button" onClick={handleSave} disabled={saving}>
                  {saving ? <ButtonContent><LoadingSpinner />Saving...</ButtonContent> : "Save"}
                </SearchButton>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={saving}
                  style={{
                    padding: "0.5rem 1rem",
                    fontSize: "0.875rem",
                    background: "none",
                    border: "1px solid var(--border-color)",
                    borderRadius: "5px",
                    cursor: "pointer",
                    color: "var(--text-color)"
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <PatientInfoContent>
              <PatientInfoItem>
                <PatientInfoLabel>Name:</PatientInfoLabel>
                <PatientInfoValue>{formData.patientName}</PatientInfoValue>
              </PatientInfoItem>
              <PatientInfoItem>
                <PatientInfoLabel>Email:</PatientInfoLabel>
                <PatientInfoValue>{formData.patientEmail}</PatientInfoValue>
              </PatientInfoItem>
              <PatientInfoItem>
                <PatientInfoLabel>Phone:</PatientInfoLabel>
                <PatientInfoValue>{formData.patientPhone}</PatientInfoValue>
              </PatientInfoItem>
              <PatientInfoItem>
                <PatientInfoLabel>Date Of Birth:</PatientInfoLabel>
                <PatientInfoValue>{formData.patientDOB || "NA"}</PatientInfoValue>
              </PatientInfoItem>
              <PatientInfoItem>
                <PatientInfoLabel>Gender:</PatientInfoLabel>
                <PatientInfoValue>{GENDER_DISPLAY[formData.patientGender] || "Unknown"}</PatientInfoValue>
              </PatientInfoItem>
            </PatientInfoContent>
          )}
        </PatientInfoCard>
      )}
    </FormSection>
  );
};

export default PatientInfoSection;
